import { Alert, Chip, Grid, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { z } from "zod";

import { useAuth } from "../auth/AuthProvider";
import { ErrorState, LoadingState, PageHeader, StatusChip } from "../components";
import { applyServerErrors, FormNumber, FormSection, FormSelect, FormShell, FormText, useAppForm } from "../components/Form";
import help from "./HELP.md?raw";
import { useOrganization, useUpdateOrganization } from "./useData";

const schema = z.object({
  name: z.string().min(2).max(200),
  timezone: z.string().min(1),
  locale: z.string().min(2),
  base_currency: z.string().length(3),
  logo_url: z.string().url().or(z.literal("")),
  security: z.object({
    password_min_length: z.coerce.number().min(8).max(64),
    require_mfa: z.enum(["true", "false"]),
    session_hours: z.coerce.number().min(1).max(720),
    idle_timeout_minutes: z.coerce.number().min(0).max(1440),
    allowed_email_domains: z.string(),
  }),
});
type Values = z.infer<typeof schema>;
const TIMEZONES = Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : ["UTC"];
const CURRENCIES = ["USD", "CAD", "EUR", "GBP", "MXN", "AUD", "JPY"];

export function OrganizationSettings() {
  const { hasPermission } = useAuth();
  const org = useOrganization();
  const update = useUpdateOrganization();
  const [error, setError] = useState<string | null>(null);
  const canEdit = hasPermission("accounts.organization.change");
  const form = useAppForm<Values>(schema, { defaultValues: { name: "", timezone: "UTC", locale: "en-US", base_currency: "USD", logo_url: "", security: { password_min_length: 10, require_mfa: "false", session_hours: 12, idle_timeout_minutes: 0, allowed_email_domains: "" } } });

  useEffect(() => {
    if (org.data) form.reset({
      name: org.data.name, timezone: org.data.timezone, locale: org.data.locale, base_currency: org.data.base_currency, logo_url: org.data.logo_url ?? "",
      security: { ...org.data.security, require_mfa: org.data.security.require_mfa ? "true" : "false", allowed_email_domains: org.data.security.allowed_email_domains.join(", ") },
    });
  }, [org.data, form]);

  async function onSubmit(v: Values) {
    setError(null);
    try {
      await update.mutateAsync({
        name: v.name, timezone: v.timezone, locale: v.locale, base_currency: v.base_currency.toUpperCase(), logo_url: v.logo_url,
        security: { ...v.security, require_mfa: v.security.require_mfa === "true", allowed_email_domains: v.security.allowed_email_domains.split(/[\s,]+/).filter(Boolean) },
      });
      form.reset(v);
    } catch (err) {
      setError(applyServerErrors(form, err));
    }
  }

  if (org.isLoading) return <LoadingState />;
  if (org.isError) return <ErrorState message={org.error.message} onRetry={() => void org.refetch()} />;
  return (
    <>
      <PageHeader help={help} title="Organization" crumbs={[{ label: "Settings" }, { label: "Organization" }]}
        actions={<Stack direction="row" spacing={1} alignItems="center"><StatusChip status={org.data!.status} /><Chip label={org.data!.slug} size="small" variant="outlined" /></Stack>} />
      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You can view these settings but only administrators can change them.</Alert>}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 9 }}>
          <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
            <FormShell form={form} onSubmit={onSubmit} error={error} busy={update.isPending}>
              <FormSection title="Company">
                <FormText name="name" label="Company name" />
                <FormText name="logo_url" label="Logo URL" helperText="Shown on documents and in the app header." />
                <FormSelect name="timezone" label="Default timezone" options={TIMEZONES.map((t) => ({ value: t, label: t }))} />
                <FormSelect name="base_currency" label="Base currency" options={CURRENCIES.map((c) => ({ value: c, label: c }))} />
                <FormText name="locale" label="Default locale" />
                <TextField label="Organization ID" value={org.data!.slug} disabled helperText="Used in API requests (X-Organization)." />
              </FormSection>
              <FormSection title="Security policy">
                <FormNumber name="security.password_min_length" label="Minimum password length" />
                <FormSelect name="security.require_mfa" label="Two-factor authentication" options={[{ value: "false", label: "Optional" }, { value: "true", label: "Required for all members" }]} />
                <FormNumber name="security.session_hours" label="Session lifetime (hours)" />
                <FormNumber name="security.idle_timeout_minutes" label="Idle timeout (minutes, 0 = off)" />
                <FormText name="security.allowed_email_domains" label="Allowed email domains" helperText="Comma-separated. Empty = any domain may be invited." />
              </FormSection>
            </FormShell>
          </fieldset>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="body2" color="text.secondary">Plan, billing and data export live under Billing (coming with CF-0067). Closing the organization is handled by support until self-serve closure ships.</Typography>
        </Grid>
      </Grid>
    </>
  );
}
